import { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Button, Input, Switch } from '@artstorefronts/ui';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BookOpen,
  Circle,
  ClipboardList,
  Frame,
  GripVertical,
  House,
  Images,
  Megaphone,
  Sparkles,
  Star,
  Sunrise,
  Trophy,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';

const NAV_CONFIG = gql`
  query NavConfig {
    navConfig {
      schemaVersion
      apps {
        id
        label
        icon
        href
        isEnabled
      }
    }
  }
`;

// The operation name has to match the mutation field — the API's zod plugin looks the
// field's `validate` extension up by operation name, and skips validation without it.
const UPDATE_NAV_CONFIG = gql`
  mutation updateNavConfig($input: UpdateNavConfigInput!) {
    updateNavConfig(input: $input) {
      schemaVersion
      apps {
        id
        label
        icon
        href
        isEnabled
      }
    }
  }
`;

// `isEnabled` is the GraphQL spelling. The JSONB column and the public
// `GET /rest/nav-config` payload both say `enabled`; the API resolver maps between them,
// so this editor only ever sees the GraphQL name.
interface NavConfigApp {
  id: string;
  label: string;
  icon: string;
  href: string;
  isEnabled: boolean;
}

interface NavConfigDraft {
  apps: NavConfigApp[];
}

/**
 * Icons are lucide names stored on the row, not uploads — this map is the whole
 * vocabulary. An unknown name falls back to a neutral dot rather than crashing the row.
 */
const ICONS: Record<string, typeof House> = {
  house: House,
  sunrise: Sunrise,
  star: Star,
  sparkles: Sparkles,
  frame: Frame,
  'book-open': BookOpen,
  megaphone: Megaphone,
  'clipboard-list': ClipboardList,
  trophy: Trophy,
  images: Images,
};

/**
 * The bar's palette is compiled into `packages/nav`, not configured — this chip only
 * previews which rows are switched on, so it repeats the package's accent rather than
 * reading a value that no longer exists. Static now that nobody types the colour, so it
 * is a utility class again instead of the interpolated inline style it had to be before.
 */
const ENABLED_CHIP = 'bg-gradient-to-b from-[#3dbe66] to-[#2aa34f] text-white';
const DISABLED_CHIP = 'bg-[#f1ede2] text-[#4a5951]';

function toDraft(config: NavConfigDraft): NavConfigDraft {
  return { apps: config.apps.map((app) => ({ ...app })) };
}

// Every field is picked explicitly, never spread or passed through by reference. The
// draft is seeded from `data.navConfig`, whose objects carry Apollo's `__typename` — the
// cache runs with the default `addTypename: true` and the client installs no
// `removeTypenameFromVariables` link — and the server rejects the whole operation with
// `Field "__typename" is not defined by type "NavConfigAppInput"`. Picking fields also
// fixes key order, which the `dirty` JSON comparison below depends on.
function toInput(draft: NavConfigDraft) {
  return {
    apps: draft.apps.map((app) => ({
      id: app.id,
      label: app.label,
      icon: app.icon,
      href: app.href,
      isEnabled: app.isEnabled,
    })),
  };
}

interface AppRowProps {
  app: NavConfigApp;
  onChange: (id: string, patch: Partial<NavConfigApp>) => void;
}

function AppRow({ app, onChange }: AppRowProps) {
  const { t } = useTranslation('admin');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: app.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const Icon = ICONS[app.icon] ?? Circle;
  // Same rule the server enforces: nothing can be shown in the bar with nowhere to go.
  const hasHref = app.href.trim().length > 0;
  const chipClass = app.isEnabled ? ENABLED_CHIP : DISABLED_CHIP;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 border-b border-gray-100 bg-white py-2.5 last:border-b-0"
    >
      <span
        {...attributes}
        {...listeners}
        title={t('nav-config.drag-hint')}
        className="flex cursor-grab touch-none items-center text-gray-400 active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </span>

      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${chipClass}`}>
        <Icon className="h-4 w-4" />
      </span>

      <span className="flex w-44 shrink-0 flex-col gap-0.5">
        <Input
          type="text"
          value={app.label}
          aria-label={t('nav-config.label-label')}
          placeholder={t('nav-config.label-placeholder')}
          onChange={(e) => onChange(app.id, { label: e.target.value })}
          className="h-8 w-full text-sm font-medium"
        />
        <span className="font-mono text-xs text-gray-400">{app.id}</span>
      </span>

      <span className="min-w-0 flex-1">
        <Input
          type="text"
          value={app.href}
          placeholder={t('nav-config.destination-placeholder')}
          onChange={(e) => onChange(app.id, { href: e.target.value })}
          className="h-8 w-full text-sm"
        />
      </span>

      <span className="flex shrink-0 items-center gap-3">
        {!hasHref && <span className="text-sm text-gray-500">{t('nav-config.destination-required')}</span>}
        <Switch
          checked={app.isEnabled}
          disabled={!hasHref}
          onCheckedChange={(checked) => onChange(app.id, { isEnabled: checked })}
        />
      </span>
    </div>
  );
}

function NavConfigPage() {
  const { t } = useTranslation('admin');
  const { data, loading } = useQuery(NAV_CONFIG);
  // `NavConfig` exposes no `id`, so Apollo cannot normalize it: the result lands at
  // `ROOT_MUTATION.updateNavConfig` and `ROOT_QUERY.navConfig` would keep the pre-save
  // value forever — leaving `dirty` true after a successful save and making Revert
  // reinstate the old config over the new one. Note `NavConfigApp` DOES have an `id` and
  // is normalized, so an apps-only edit settles on its own — the stale half is
  // `schemaVersion`, which sits embedded under ROOT_QUERY. The mutation selects exactly the query's
  // field set, so its result is a complete `navConfig` and writing it back is enough; keep
  // the two selection sets identical. Preferred over `refetchQueries` because it needs no
  // extra round trip and the write is deterministic.
  const [updateNavConfig, { loading: saving }] = useMutation(UPDATE_NAV_CONFIG, {
    update: (cache, { data: result }) => {
      if (!result?.updateNavConfig) return;
      cache.writeQuery({ query: NAV_CONFIG, data: { navConfig: result.updateNavConfig } });
    },
  });
  const [draft, setDraft] = useState<NavConfigDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const persisted: NavConfigDraft | null = data?.navConfig ?? null;

  useEffect(() => {
    if (!persisted) return;
    setDraft(toDraft(persisted));
  }, [persisted]);

  // A save is live immediately, so the only guard is "does this differ from what is
  // stored" — comparing against the persisted value, not tracking edits.
  const dirty = !!draft && !!persisted && JSON.stringify(toInput(draft)) !== JSON.stringify(toInput(persisted));

  const setApp = (id: string, patch: Partial<NavConfigApp>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const apps = prev.apps.map((app) => {
        if (app.id !== id) return app;
        const next = { ...app, ...patch };
        // Clearing the destination has to clear the toggle too, or the draft carries a
        // combination the server will reject on save.
        if (next.href.trim().length === 0) next.isEnabled = false;
        return next;
      });
      return { ...prev, apps };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const oldIndex = prev.apps.findIndex((app) => app.id === active.id);
      const newIndex = prev.apps.findIndex((app) => app.id === over.id);
      return { ...prev, apps: arrayMove(prev.apps, oldIndex, newIndex) };
    });
  };

  const handleSave = async () => {
    if (!draft || !dirty) return;
    setError(null);
    try {
      await updateNavConfig({ variables: { input: toInput(draft) } });
    } catch (err: any) {
      setError(err?.message || 'Error');
    }
  };

  const handleRevert = () => {
    if (!persisted) return;
    setError(null);
    setDraft(toDraft(persisted));
  };

  return (
    <AdminLayout title={t('nav-config.page-title')} description={t('nav-config.page-description')}>
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold">{t('nav-config.section-heading')}</h2>

        {loading || !draft ? (
          <div>{t('nav-config.loading')}</div>
        ) : (
          <div className="grid gap-4">
            <div className="space-y-3 rounded-lg bg-white p-4 shadow">
              <div>
                <h3 className="font-medium">{t('nav-config.apps-heading')}</h3>
                <p className="font-mono text-xs text-gray-400">nav_config.apps</p>
                <p className="mt-1 text-sm text-gray-500">{t('nav-config.apps-help')}</p>
              </div>
              <div>
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={draft.apps.map((app) => app.id)} strategy={verticalListSortingStrategy}>
                    {draft.apps.map((app) => (
                      <AppRow key={app.id} app={app} onChange={setApp} />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button type="button" onClick={handleSave} disabled={!dirty || saving}>
                {t('nav-config.save')}
              </Button>
              <Button type="button" variant="outline" onClick={handleRevert} disabled={!dirty || saving}>
                {t('nav-config.revert')}
              </Button>
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default NavConfigPage;
